import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import {
  FileText,
  FileType,
  Link as LinkIcon,
  Plus,
  Search,
  Upload,
  LayoutGrid,
  List as ListIcon,
  Archive,
  ArchiveRestore,
  Trash2,
  ExternalLink,
  X,
  Save,
  Edit3,
  Library,
  Folder,
  Tag as TagIcon,
  Pin,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import PdfViewer from "../components/resources/PdfViewer";
import DocumentEditor from "../components/resources/DocumentEditor";

const CATEGORIES = [
  "SOP", "Finance", "Sales", "Production", "PM",
  "Legal", "Branding", "Marketing", "VEXOS", "Templates",
  "HR", "Internal Knowledge", "Other",
];

const VIEWS = [
  { key: "all", label: "All Resources", filter: {} },
  { key: "pdf", label: "PDFs", filter: { type: "pdf" } },
  { key: "document", label: "Documents", filter: { type: "document" } },
  { key: "link", label: "Links", filter: { type: "link" } },
  { key: "sops", label: "SOPs", filter: { category: "SOP" } },
  { key: "templates", label: "Templates", filter: { category: "Templates" } },
  { key: "projects", label: "Project Resources", filter: { hasProject: true } },
  { key: "archived", label: "Archived", filter: { archived: true } },
];

const typeMeta = {
  pdf: { label: "PDF", Icon: FileType, color: "#C9835F" },
  document: { label: "Document", Icon: FileText, color: "#C9C6C1" },
  link: { label: "Link", Icon: LinkIcon, color: "#4CA173" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return "—"; }
}

export default function ResourcesLibrary() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get("view") || "all");
  const [layout, setLayout] = useState("grid"); // 'grid' | 'table'
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // currently opened resource
  const [programs, setPrograms] = useState([]);

  // Create dialogs
  const [showUpload, setShowUpload] = useState(false);
  const [showNewDoc, setShowNewDoc] = useState(false);
  const [showNewLink, setShowNewLink] = useState(false);

  // Delete confirm
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  const activeView = useMemo(() => VIEWS.find(v => v.key === view) || VIEWS[0], [view]);

  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (activeView.filter.type) params.type = activeView.filter.type;
      if (activeView.filter.category) params.category = activeView.filter.category;
      if (activeView.filter.archived) params.archived = true;
      if (search) params.search = search;
      if (category && category !== "all") params.category = category;
      const res = await apiClient.get("/resources/", { params });
      let data = res.data || [];
      if (activeView.filter.hasProject) {
        data = data.filter(r => r.related_project_id);
      }
      setResources(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load resources");
    } finally {
      setLoading(false);
    }
  }, [activeView, search, category]);

  useEffect(() => { fetchResources(); }, [fetchResources]);

  useEffect(() => {
    apiClient.get("/programs").then(r => setPrograms(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setSearchParams(view === "all" ? {} : { view });
  }, [view, setSearchParams]);

  const handleOpen = (r) => setSelected(r);
  const handleClose = () => setSelected(null);

  const refreshAndClose = async () => {
    await fetchResources();
    setSelected(null);
  };

  const doArchive = async (r) => {
    try {
      if (r.archived_at) await apiClient.post(`/resources/${r.id}/unarchive`);
      else await apiClient.post(`/resources/${r.id}/archive`);
      toast.success(r.archived_at ? "Restored" : "Archived");
      await fetchResources();
      if (selected?.id === r.id) setSelected(null);
    } catch {
      toast.error("Action failed");
    }
  };

  const doDelete = async () => {
    if (!deleteCandidate) return;
    try {
      await apiClient.delete(`/resources/${deleteCandidate.id}`);
      toast.success("Deleted");
      setDeleteCandidate(null);
      if (selected?.id === deleteCandidate.id) setSelected(null);
      await fetchResources();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#000000] text-[#F4F2EE]" data-testid="resources-library">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#8A8783] mb-2">
              <Library className="w-3 h-3" />
              <span>Internal Library</span>
            </div>
            <h1 className="text-[24px] sm:text-[32px] font-light tracking-tight">Resources</h1>
            <p className="text-[14px] text-[#8A8783] mt-1 max-w-xl">
              Centralized knowledge hub for SOPs, agreements, templates, internal docs, and links.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => setShowUpload(true)}
              className="bg-white text-[#000000] hover:bg-white/90"
              data-testid="btn-upload-pdf"
            >
              <Upload className="w-4 h-4 mr-2" /> Upload PDF
            </Button>
            <Button
              onClick={() => setShowNewDoc(true)}
              variant="outline"
              className="border-white/[0.14] bg-transparent text-white hover:bg-white/[0.06]"
              data-testid="btn-new-document"
            >
              <Plus className="w-4 h-4 mr-2" /> New Document
            </Button>
            <Button
              onClick={() => setShowNewLink(true)}
              variant="outline"
              className="border-white/[0.14] bg-transparent text-white hover:bg-white/[0.06]"
              data-testid="btn-add-link"
            >
              <LinkIcon className="w-4 h-4 mr-2" /> Add Link
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
          {/* Sidebar view filters */}
          <aside className="space-y-1" data-testid="resources-sidebar">
            {VIEWS.map(v => (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-[13px] transition-colors ${
                  view === v.key
                    ? "bg-white/[0.06] text-white"
                    : "text-[#8A8783] hover:bg-white/[0.03] hover:text-[#D5D3CE]"
                }`}
                data-testid={`view-${v.key}`}
              >
                <span>{v.label}</span>
              </button>
            ))}

            <div className="pt-4 mt-4 border-t border-white/[0.06]">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#565350] px-3 mb-2">Category</div>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-transparent border-white/[0.08] text-[13px] text-[#D5D3CE]" data-testid="category-filter">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent className="bg-[#000000] border-white/[0.08] text-white">
                  <SelectItem value="all">All categories</SelectItem>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </aside>

          {/* Main column */}
          <div>
            {/* Search + layout toggle */}
            <div className="flex items-center justify-between mb-5">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#565350]" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by title, tag, or description…"
                  className="pl-9 bg-[#000000] border-white/[0.08] text-[#F4F2EE] placeholder:text-[#565350]"
                  data-testid="search-input"
                />
              </div>
              <div className="flex items-center gap-1 bg-white/[0.04] p-1 rounded-lg ml-4">
                <button
                  onClick={() => setLayout("grid")}
                  className={`p-1.5 rounded ${layout === "grid" ? "bg-white/10 text-white" : "text-[#8A8783]"}`}
                  data-testid="layout-grid"
                  aria-label="Grid view"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setLayout("table")}
                  className={`p-1.5 rounded ${layout === "table" ? "bg-white/10 text-white" : "text-[#8A8783]"}`}
                  data-testid="layout-table"
                  aria-label="Table view"
                >
                  <ListIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Results */}
            {loading ? (
              <div className="text-center py-20 text-[#565350] text-sm">Loading…</div>
            ) : resources.length === 0 ? (
              <EmptyState view={activeView} onUpload={() => setShowUpload(true)} onDoc={() => setShowNewDoc(true)} />
            ) : layout === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {resources.map(r => (
                  <ResourceCard key={r.id} r={r} onOpen={handleOpen} />
                ))}
              </div>
            ) : (
              <ResourceTable resources={resources} onOpen={handleOpen} />
            )}
          </div>
        </div>
      </div>

      {/* Create dialogs */}
      <UploadPdfDialog
        open={showUpload}
        onClose={() => setShowUpload(false)}
        programs={programs}
        onCreated={() => { setShowUpload(false); fetchResources(); }}
      />
      <NewDocumentDialog
        open={showNewDoc}
        onClose={() => setShowNewDoc(false)}
        programs={programs}
        onCreated={(r) => { setShowNewDoc(false); fetchResources(); setSelected(r); }}
      />
      <NewLinkDialog
        open={showNewLink}
        onClose={() => setShowNewLink(false)}
        programs={programs}
        onCreated={() => { setShowNewLink(false); fetchResources(); }}
      />

      {/* Detail slideover */}
      {selected && (
        <ResourceDetailPanel
          resource={selected}
          programs={programs}
          onClose={handleClose}
          onSaved={refreshAndClose}
          onArchiveToggle={() => doArchive(selected)}
          onDelete={() => setDeleteCandidate(selected)}
        />
      )}

      {/* Delete confirm */}
      <AlertDialog open={!!deleteCandidate} onOpenChange={(open) => { if (!open) setDeleteCandidate(null); }}>
        <AlertDialogContent className="bg-[#000000] border-white/[0.08] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete resource?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#8A8783]">
              "{deleteCandidate?.title}" will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/[0.08] text-white hover:bg-white/[0.06]">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-500 hover:bg-red-600 text-white" onClick={doDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------- Sub-components ----------

function EmptyState({ view, onUpload, onDoc }) {
  return (
    <div className="text-center py-20 border border-dashed border-white/[0.08] rounded-2xl">
      <Library className="w-8 h-8 text-[#302E2B] mx-auto mb-3" />
      <h3 className="text-[15px] font-semibold text-[#F4F2EE]">No resources yet</h3>
      <p className="text-sm text-[#8A8783] mt-1 max-w-sm mx-auto">
        {view.key === "archived"
          ? "Nothing archived. Archived items will appear here."
          : "Upload a PDF, create a document, or add a link to get started."}
      </p>
      {view.key !== "archived" && (
        <div className="flex items-center justify-center gap-2 mt-5 flex-wrap px-4">
          <Button onClick={onUpload} className="bg-white text-[#000000] hover:bg-white/90">
            <Upload className="w-4 h-4 mr-2" /> Upload PDF
          </Button>
          <Button onClick={onDoc} variant="outline" className="border-white/[0.14] text-white bg-transparent hover:bg-white/[0.06]">
            <Plus className="w-4 h-4 mr-2" /> New Document
          </Button>
        </div>
      )}
    </div>
  );
}

function ResourceCard({ r, onOpen }) {
  const meta = typeMeta[r.type] || typeMeta.document;
  const Icon = meta.Icon;
  return (
    <button
      onClick={() => onOpen(r)}
      className="text-left rounded-2xl border border-white/[0.08] bg-[#000000] hover:border-white/[0.16] hover:bg-[#121212] transition-all p-5"
      data-testid={`resource-card-${r.id}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-full bg-white/[0.04] flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4" style={{ color: meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.1em] text-[#8A8783]">{meta.label}</div>
          <div className="text-[15px] font-semibold text-[#F4F2EE] mt-0.5 line-clamp-2">{r.title}</div>
        </div>
      </div>
      {r.description && (
        <p className="text-[12px] text-[#8A8783] line-clamp-2 mb-3">{r.description}</p>
      )}
      <div className="flex items-center justify-between text-[11px] text-[#565350]">
        <span className="inline-flex items-center gap-1">
          <Folder className="w-3 h-3" /> {r.category || "Other"}
        </span>
        <span>Updated {formatDate(r.updated_at)}</span>
      </div>
      {r.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {r.tags.slice(0, 4).map(t => (
            <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] text-[#C9C6C1]">{t}</span>
          ))}
        </div>
      )}
    </button>
  );
}

function ResourceTable({ resources, onOpen }) {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-[#000000] overflow-x-auto">
      <table className="w-full text-[13px] min-w-[720px]" data-testid="resource-table">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.08em] text-[#565350] border-b border-white/[0.06]">
            <th className="px-4 py-3 font-medium">Title</th>
            <th className="px-4 py-3 font-medium">Type</th>
            <th className="px-4 py-3 font-medium">Category</th>
            <th className="px-4 py-3 font-medium">Project</th>
            <th className="px-4 py-3 font-medium">Owner</th>
            <th className="px-4 py-3 font-medium">Updated</th>
          </tr>
        </thead>
        <tbody>
          {resources.map(r => {
            const meta = typeMeta[r.type] || typeMeta.document;
            return (
              <tr
                key={r.id}
                onClick={() => onOpen(r)}
                className="border-b border-white/[0.04] hover:bg-white/[0.03] cursor-pointer"
                data-testid={`resource-row-${r.id}`}
              >
                <td className="px-4 py-3 text-[#F4F2EE] font-medium">{r.title}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 text-[#C9C6C1]">
                    <meta.Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                    {meta.label}
                  </span>
                </td>
                <td className="px-4 py-3 text-[#C9C6C1]">{r.category || "—"}</td>
                <td className="px-4 py-3 text-[#8A8783]">{r.related_project_name || "—"}</td>
                <td className="px-4 py-3 text-[#8A8783]">{r.owner_name || "—"}</td>
                <td className="px-4 py-3 text-[#8A8783]">{formatDate(r.updated_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Upload / Create Dialogs ----------

function UploadPdfDialog({ open, onClose, programs, onCreated }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Other");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [projectId, setProjectId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setFile(null); setTitle(""); setCategory("Other");
      setDescription(""); setTags(""); setProjectId("");
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!file) { toast.error("Choose a PDF"); return; }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("title", title || file.name.replace(/\.pdf$/i, ""));
      form.append("category", category);
      if (description) form.append("description", description);
      if (tags) form.append("tags", tags);
      if (projectId) form.append("related_project_id", projectId);
      form.append("visibility", "team");
      await apiClient.post("/resources/upload-pdf", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF uploaded");
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#000000] border-white/[0.08] text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload PDF</DialogTitle>
          <DialogDescription className="text-[#8A8783]">
            PDFs are previewed inline in TRP HQ — no downloading required.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8A8783] mb-1.5 block">File</Label>
            <Input
              type="file"
              accept="application/pdf"
              onChange={(e) => {
                const f = e.target.files?.[0];
                setFile(f);
                if (f && !title) setTitle(f.name.replace(/\.pdf$/i, ""));
              }}
              className="bg-[#000000] border-white/[0.08] file:text-[#C9C6C1] text-[#F4F2EE]"
              data-testid="upload-file-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8A8783] mb-1.5 block">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-[#000000] border-white/[0.08] text-white" data-testid="upload-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#8A8783] mb-1.5 block">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-[#000000] border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#000000] border-white/[0.08] text-white">
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#8A8783] mb-1.5 block">Project (optional)</Label>
              <Select value={projectId || "none"} onValueChange={(v) => setProjectId(v === "none" ? "" : v)}>
                <SelectTrigger className="bg-[#000000] border-white/[0.08] text-white"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-[#000000] border-white/[0.08] text-white">
                  <SelectItem value="none">None</SelectItem>
                  {programs.map(p => <SelectItem key={p.id} value={p.id}>{p.program_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8A8783] mb-1.5 block">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="bg-[#000000] border-white/[0.08] text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8A8783] mb-1.5 block">Tags (comma separated)</Label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="onboarding, policy, Q1" className="bg-[#000000] border-white/[0.08] text-white" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#C9C6C1]">Cancel</Button>
          <Button disabled={submitting || !file} onClick={handleSubmit} className="bg-white text-[#000000] hover:bg-white/90" data-testid="upload-submit">
            {submitting ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewDocumentDialog({ open, onClose, programs, onCreated }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Internal Knowledge");
  const [projectId, setProjectId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setTitle(""); setCategory("Internal Knowledge"); setProjectId("");
    }
  }, [open]);

  const handleCreate = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSubmitting(true);
    try {
      const res = await apiClient.post("/resources/", {
        title,
        type: "document",
        category,
        text_content: "",
        related_project_id: projectId || null,
        visibility: "team",
      });
      toast.success("Document created");
      onCreated?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#000000] border-white/[0.08] text-white max-w-md">
        <DialogHeader>
          <DialogTitle>New Document</DialogTitle>
          <DialogDescription className="text-[#8A8783]">Write SOPs, internal notes, or process docs.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8A8783] mb-1.5 block">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Onboarding SOP" className="bg-[#000000] border-white/[0.08] text-white" data-testid="new-doc-title" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8A8783] mb-1.5 block">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="bg-[#000000] border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#000000] border-white/[0.08] text-white">
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8A8783] mb-1.5 block">Project (optional)</Label>
            <Select value={projectId || "none"} onValueChange={(v) => setProjectId(v === "none" ? "" : v)}>
              <SelectTrigger className="bg-[#000000] border-white/[0.08] text-white"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent className="bg-[#000000] border-white/[0.08] text-white">
                <SelectItem value="none">None</SelectItem>
                {programs.map(p => <SelectItem key={p.id} value={p.id}>{p.program_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#C9C6C1]">Cancel</Button>
          <Button disabled={submitting || !title.trim()} onClick={handleCreate} className="bg-white text-[#000000] hover:bg-white/90" data-testid="new-doc-submit">
            {submitting ? "Creating…" : "Create & Open"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewLinkDialog({ open, onClose, programs, onCreated }) {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [category, setCategory] = useState("Other");
  const [projectId, setProjectId] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setTitle(""); setUrl(""); setCategory("Other"); setProjectId(""); setDescription("");
    }
  }, [open]);

  const handleCreate = async () => {
    if (!title.trim() || !url.trim()) { toast.error("Title and URL are required"); return; }
    setSubmitting(true);
    try {
      await apiClient.post("/resources/", {
        title,
        type: "link",
        link_url: url,
        category,
        description,
        related_project_id: projectId || null,
        visibility: "team",
      });
      toast.success("Link added");
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#000000] border-white/[0.08] text-white max-w-md">
        <DialogHeader>
          <DialogTitle>Add Link</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8A8783] mb-1.5 block">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-[#000000] border-white/[0.08] text-white" data-testid="new-link-title" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8A8783] mb-1.5 block">URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="bg-[#000000] border-white/[0.08] text-white" data-testid="new-link-url" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#8A8783] mb-1.5 block">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-[#000000] border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#000000] border-white/[0.08] text-white">
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#8A8783] mb-1.5 block">Project</Label>
              <Select value={projectId || "none"} onValueChange={(v) => setProjectId(v === "none" ? "" : v)}>
                <SelectTrigger className="bg-[#000000] border-white/[0.08] text-white"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-[#000000] border-white/[0.08] text-white">
                  <SelectItem value="none">None</SelectItem>
                  {programs.map(p => <SelectItem key={p.id} value={p.id}>{p.program_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8A8783] mb-1.5 block">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="bg-[#000000] border-white/[0.08] text-white" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#C9C6C1]">Cancel</Button>
          <Button disabled={submitting} onClick={handleCreate} className="bg-white text-[#000000] hover:bg-white/90" data-testid="new-link-submit">
            {submitting ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Detail panel (slide-over) ----------

function ResourceDetailPanel({ resource, programs, onClose, onSaved, onArchiveToggle, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(resource.title);
  const [description, setDescription] = useState(resource.description || "");
  const [category, setCategory] = useState(resource.category || "Other");
  const [tags, setTags] = useState((resource.tags || []).join(", "));
  const [projectId, setProjectId] = useState(resource.related_project_id || "");
  const [textContent, setTextContent] = useState(resource.text_content || "");
  const [linkUrl, setLinkUrl] = useState(resource.link_url || "");
  const [shareByDefault, setShareByDefault] = useState(!!resource.share_with_clients_by_default);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title,
        description,
        category,
        tags: tags.split(",").map(t => t.trim()).filter(Boolean),
        related_project_id: projectId || null,
        share_with_clients_by_default: shareByDefault,
      };
      if (resource.type === "document") payload.text_content = textContent;
      if (resource.type === "link") payload.link_url = linkUrl;
      await apiClient.patch(`/resources/${resource.id}`, payload);
      toast.success("Saved");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const typeMetaItem = typeMeta[resource.type] || typeMeta.document;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="resource-detail-panel">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative ml-auto h-full w-full max-w-5xl bg-[#000000] border-l border-white/[0.08] shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-white/[0.04] flex items-center justify-center">
              <typeMetaItem.Icon className="w-4 h-4" style={{ color: typeMetaItem.color }} />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.12em] text-[#8A8783]">{typeMetaItem.label}</div>
              {editing ? (
                <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-transparent border-none text-[18px] font-light px-0 h-auto focus-visible:ring-0" />
              ) : (
                <div className="text-[18px] font-light text-[#F4F2EE] truncate">{resource.title}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!editing ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="text-[#C9C6C1]" data-testid="detail-edit-btn">
                  <Edit3 className="w-4 h-4 mr-1.5" /> Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={onArchiveToggle} className="text-[#C9C6C1]">
                  {resource.archived_at ? <><ArchiveRestore className="w-4 h-4 mr-1.5" /> Restore</> : <><Archive className="w-4 h-4 mr-1.5" /> Archive</>}
                </Button>
                <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-white text-[#000000] hover:bg-white/90" data-testid="detail-save-btn">
                <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} className="text-[#C9C6C1]">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-0 h-full">
            {/* Main content */}
            <div className="p-6 min-h-0">
              {resource.type === "pdf" && (
                <PdfViewer resourceId={resource.id} fileName={resource.file_name} compact />
              )}
              {resource.type === "document" && (
                editing ? (
                  <DocumentEditor value={textContent} onChange={setTextContent} />
                ) : (
                  <div className="rounded-xl border border-white/[0.08] bg-[#000000] px-6 py-5">
                    <DocumentEditor value={resource.text_content || ""} onChange={() => {}} readOnly />
                  </div>
                )
              )}
              {resource.type === "link" && (
                <div className="rounded-2xl border border-white/[0.08] bg-[#000000] p-6">
                  {editing ? (
                    <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} className="bg-[#000000] border-white/[0.08] text-white" />
                  ) : (
                    <a
                      href={resource.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between gap-3 text-[#F4F2EE] hover:text-white group"
                      data-testid="link-open"
                    >
                      <span className="text-[15px] truncate">{resource.link_url}</span>
                      <span className="inline-flex items-center gap-1.5 text-[12px] text-[#8FA79B] opacity-80 group-hover:opacity-100">
                        Open <ExternalLink className="w-3.5 h-3.5" />
                      </span>
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Metadata sidebar */}
            <aside className="border-l border-white/[0.06] p-6 space-y-5">
              <MetaBlock label="Category">
                {editing ? (
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="bg-[#000000] border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#000000] border-white/[0.08] text-white">
                      {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-[13px] text-[#F4F2EE]">{resource.category || "—"}</span>
                )}
              </MetaBlock>

              <MetaBlock label="Project">
                {editing ? (
                  <Select value={projectId || "none"} onValueChange={(v) => setProjectId(v === "none" ? "" : v)}>
                    <SelectTrigger className="bg-[#000000] border-white/[0.08] text-white"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent className="bg-[#000000] border-white/[0.08] text-white max-h-64">
                      <SelectItem value="none">None</SelectItem>
                      {programs.map(p => <SelectItem key={p.id} value={p.id}>{p.program_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-[13px] text-[#F4F2EE]">{resource.related_project_name || "—"}</span>
                )}
              </MetaBlock>

              {/* Attaching a resource to every NEW project dashboard. Deliberately
                  says "new": turning this on does not publish the document to
                  clients already holding a portal link. */}
              <MetaBlock label="Client projects">
                {editing ? (
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={shareByDefault} className="mt-0.5"
                      onChange={(e) => setShareByDefault(e.target.checked)} />
                    <span className="text-[13px] text-[#D5D3CE]">
                      Share on every new client project dashboard
                      <span className="block text-[11px] text-[#8A8783] mt-0.5">
                        Existing projects are untouched — a PM adds it there with “Add defaults”.
                      </span>
                    </span>
                  </label>
                ) : (
                  <span className="text-[13px] text-[#F4F2EE]">
                    {resource.share_with_clients_by_default
                      ? "Shared on every new project dashboard"
                      : "Not shared by default"}
                  </span>
                )}
              </MetaBlock>

              <MetaBlock label="Description">
                {editing ? (
                  <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="bg-[#000000] border-white/[0.08] text-white" />
                ) : (
                  <span className="text-[13px] text-[#D5D3CE] whitespace-pre-wrap">{resource.description || "—"}</span>
                )}
              </MetaBlock>

              <MetaBlock label="Tags">
                {editing ? (
                  <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="comma, separated" className="bg-[#000000] border-white/[0.08] text-white" />
                ) : (
                  resource.tags?.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {resource.tags.map(t => (
                        <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-white/[0.04] text-[#C9C6C1] inline-flex items-center gap-1">
                          <TagIcon className="w-2.5 h-2.5" />{t}
                        </span>
                      ))}
                    </div>
                  ) : <span className="text-[13px] text-[#8A8783]">—</span>
                )}
              </MetaBlock>

              <MetaBlock label="Owner">
                <span className="text-[13px] text-[#C9C6C1]">{resource.owner_name || "—"}</span>
              </MetaBlock>

              <MetaBlock label="Created">
                <span className="text-[13px] text-[#C9C6C1]">{formatDate(resource.created_at)}</span>
              </MetaBlock>

              <MetaBlock label="Updated">
                <span className="text-[13px] text-[#C9C6C1]">{formatDate(resource.updated_at)}</span>
              </MetaBlock>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaBlock({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.12em] text-[#565350] mb-1.5">{label}</div>
      <div>{children}</div>
    </div>
  );
}
